/**
 * EventCard Component - Karta wydarzenia dla listy
 * wojskaspecjalne.pl
 */

import React from 'react';
import { Link } from 'react-router-dom';
import type { Event } from '../../types/database.types';
import './EventCard.css';

interface EventCardProps {
  event: Event;
  showStatus?: boolean;
  compact?: boolean;
}

export const EventCard: React.FC<EventCardProps> = ({
  event,
  showStatus = false,
  compact = false,
}) => {
  const startDate = event.start_date ? new Date(event.start_date) : null;
  const endDate = event.end_date ? new Date(event.end_date) : null;
  const now = new Date();
  const isUpcoming = startDate ? startDate > now : false;
  const isPast = endDate ? endDate < now : false;
  const isFull = event.max_attendees ? event.current_attendees >= event.max_attendees : false;

  // Format dates
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pl-PL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const isSameDay = startDate && endDate ? startDate.toDateString() === endDate.toDateString() : false;

  return (
    <Link
      to={`/wydarzenia/${event.slug || event.id}`}
      className={`event-card ${compact ? 'event-card--compact' : ''}`}
    >
      {/* Cover Image or Placeholder */}
      {event.cover_image_url ? (
        <div className="event-card__image">
          <img src={event.cover_image_url} alt={event.title} />
          {/* Date Badge */}
          {startDate && (
            <div className="event-card__date-badge">
              <div className="event-card__date-day">{startDate.getDate()}</div>
              <div className="event-card__date-month">
                {new Intl.DateTimeFormat('pl-PL', { month: 'short' }).format(startDate)}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="event-card__placeholder">
          <span className="event-card__placeholder-icon"></span>
          {/* Date Badge */}
          {startDate && (
            <div className="event-card__date-badge">
              <div className="event-card__date-day">{startDate.getDate()}</div>
              <div className="event-card__date-month">
                {new Intl.DateTimeFormat('pl-PL', { month: 'short' }).format(startDate)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="event-card__content">
        {/* Status badges */}
        <div className="event-card__badges">
          {event.meeting_link && event.location_name ? (
            <span className="event-card__badge event-card__badge--hybrid">Hybrydowe</span>
          ) : event.meeting_link ? (
            <span className="event-card__badge event-card__badge--virtual">Wirtualne</span>
          ) : event.location_name ? (
            <span className="event-card__badge event-card__badge--physical">Stacjonarne</span>
          ) : null}
          {event.is_free ? (
            <span className="event-card__badge event-card__badge--free">Bezpłatne</span>
          ) : (
            <span className="event-card__badge event-card__badge--paid">
              {event.price_amount} {event.price_currency}
            </span>
          )}
          {isFull && (
            <span className="event-card__badge event-card__badge--full">Brak miejsc</span>
          )}
          {showStatus && event.status !== 'published' && (
            <span className={`event-card__badge event-card__badge--${event.status}`}>
              {event.status === 'draft' && 'Szkic'}
              {event.status === 'cancelled' && 'Anulowane'}
              {event.status === 'completed' && 'Zakończone'}
            </span>
          )}
        </div>

        {/* Category */}
        {event.category && (
          <div className="event-card__category">{event.category}</div>
        )}

        {/* Title */}
        <h3 className="event-card__title">{event.title}</h3>

        {/* Description */}
        {!compact && event.description && (
          <p className="event-card__description">
            {event.description.length > 150
              ? `${event.description.substring(0, 150)}...`
              : event.description}
          </p>
        )}

        {/* Event Details */}
        <div className="event-card__details">
          {/* Date and Time */}
          {(startDate || endDate) && (
            <div className="event-card__detail">
              <span className="event-card__detail-icon">🗓️</span>
              <span className="event-card__detail-text">
                {!startDate && endDate ? (
                  <>Do: {formatDate(endDate)}</>
                ) : !endDate && startDate ? (
                  <>Od: {formatDate(startDate)}</>
                ) : isSameDay && startDate && endDate ? (
                  <>
                    {formatDate(startDate)}, {formatTime(startDate)} - {formatTime(endDate)}
                  </>
                ) : startDate && endDate ? (
                  <>
                    {formatDate(startDate)} - {formatDate(endDate)}
                  </>
                ) : null}
              </span>
            </div>
          )}

          {/* Location */}
          {event.is_virtual ? (
            <div className="event-card__detail">
              <span className="event-card__detail-text">Online</span>
            </div>
          ) : (
            event.location_name && (
              <div className="event-card__detail">
                <span className="event-card__detail-text">
                  {event.location_city
                    ? `${event.location_name}, ${event.location_city}`
                    : event.location_name}
                </span>
              </div>
            )
          )}

          {/* Attendees */}
          {event.max_attendees && (
            <div className="event-card__detail">
              <span className="event-card__detail-text">
                {event.current_attendees} / {event.max_attendees} miejsc
              </span>
            </div>
          )}
        </div>

        {/* Tags */}
        {!compact && event.tags && event.tags.length > 0 && (
          <div className="event-card__tags">
            {event.tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="event-card__tag">
                {tag}
              </span>
            ))}
            {event.tags.length > 3 && (
              <span className="event-card__tag">+{event.tags.length - 3}</span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="event-card__footer">
          {isUpcoming && (
            <span className="event-card__status event-card__status--upcoming">
              Nadchodzące
            </span>
          )}
          {isPast && (
            <span className="event-card__status event-card__status--past">
              Zakończone
            </span>
          )}
          <span className="event-card__cta">Zobacz więcej →</span>
        </div>
      </div>
    </Link>
  );
};

